import { useState } from 'react';

/**
 * What the top bar becomes while a selection exists.
 *
 * It replaces the bar rather than stacking under it: a selection is a mode,
 * and two bars would say it is an addition.
 *
 * `new storyline` is the primary action and needs two, because a storyline
 * with one member is just an arc with an extra step. A storyline IS an arc —
 * so this writes one more arc file and the importer mints it, which is the
 * one thing the design assumed the web could not do.
 */
export function SelectionBar({
  count,
  noun = 'arc',
  canGroup = false,
  onGroup,
  onAddTo,
  onClear,
}: {
  count: number;
  /** What is selected, singular; the plural adds an s. */
  noun?: string;
  canGroup?: boolean;
  /** Absent where a selection cannot become a storyline. */
  onGroup?: (name: string) => Promise<void>;
  /** Opens the add-to dialog for the selection; absent where nothing can be written. */
  onAddTo?: () => void;
  onClear: () => void;
}) {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const commit = async () => {
    if (!name.trim() || busy || !onGroup) return;
    setBusy(true);
    try {
      await onGroup(name);
      setName('');
      setNaming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <header className="topbar selection-bar">
      <span className="t-chipLabel">
        {count} {count === 1 ? noun : `${noun}s`} selected
      </span>

      {naming ? (
        <>
          <input
            autoFocus
            className="t-chipLabel selection-name"
            value={name}
            placeholder="name the storyline…"
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void commit();
              if (e.key === 'Escape') setNaming(false);
            }}
          />
          <button type="button" className="btn-primary" disabled={!name.trim() || busy} onClick={() => void commit()}>
            {busy ? 'writing…' : 'create'}
          </button>
        </>
      ) : (
        <>
          <span className="t-metaMono selection-hint">⇧ click to extend · esc to clear</span>
          <span className="topbar-spacer" />
          {onAddTo && (
            <button type="button" className="icon-btn selection-add" onClick={onAddTo}>
              Add to…
            </button>
          )}
          {onGroup && (
            <button type="button" className="btn-primary" disabled={!canGroup} onClick={() => setNaming(true)}>
              new storyline
            </button>
          )}
        </>
      )}

      <button type="button" className="icon-btn" title="Clear" onClick={onClear}>
        ✕
      </button>
    </header>
  );
}
